"use client";

import { useEffect, useState } from "react";
import { Download, ExternalLink, FileSpreadsheet, FileText, FileType2, FileArchive, File as FileIcon } from "lucide-react";
import { useAssetUrl, useLocale } from "../../runtime/RendererRuntime";
import { t } from "../../i18n/strings";

/**
 * True on iOS (iPhone/iPad/iPod). iPadOS 13+ reports a desktop Safari/Mac user
 * agent, so we also treat a touch-capable "Mac" as iOS. Detected after mount
 * (window/navigator are client-only) so SSR stays deterministic.
 */
function useIsIOS(): boolean {
  const [isIOS, setIsIOS] = useState(false);
  useEffect(() => {
    if (typeof navigator === "undefined") return;
    const ua = navigator.userAgent || "";
    const ios =
      /iP(ad|hone|od)/.test(ua) ||
      (/Mac/.test(ua) && typeof document !== "undefined" && "ontouchend" in document);
    setIsIOS(ios);
  }, []);
  return isIOS;
}

/**
 * File preview block for a {@link DataRecordDetailRenderer} record.
 *
 * The migrated `pdfKey` is a bucket-relative storage key, despite the name —
 * for legacy reasons it can point at *any* attached file (PDF, XLSX, DOCX,
 * CSV, ZIP, …), not just PDFs. The renderer therefore:
 *   - resolves the key through the runtime asset resolver (env-aware
 *     absolute URL); empty / unresolved keys render nothing.
 *   - inspects the extension and either embeds the file in an `<iframe>`
 *     (PDFs — and only PDFs — render reliably inline) OR renders a
 *     download card with a file-type icon, the file name and the same
 *     Télécharger / Ouvrir actions. Non-PDF browsers refuse to inline,
 *     so the iframe used to render a broken / blank panel.
 *
 * This is a **client component** so it can read the renderer runtime context
 * (`useAssetUrl` → `assetResolver`). `DataRecordDetailRenderer` is a server
 * component and cannot call client hooks itself; it renders this client child.
 */
export function DataRecordPdfPreview({ pdfKey }: { pdfKey?: string | null }) {
  const src = useAssetUrl(pdfKey);
  const isIOS = useIsIOS();
  const locale = useLocale();
  if (!src) return null;

  const ext = extractExtension(pdfKey ?? src);
  const fileName = extractFileName(pdfKey ?? src);

  if (ext === "pdf") {
    // iOS Safari ignores PDF open parameters (`#view`/`#zoom`) and renders a PDF
    // embedded in an <iframe> zoomed-in and non-scrollable — the exact symptom
    // on iPhone/iPad. So on iOS (and small touch screens) we skip the iframe and
    // offer a card that opens the file in the device's NATIVE PDF viewer, which
    // gives proper fit-to-width, pinch-zoom and scrolling. Desktop keeps the
    // inline iframe preview. The toggle is responsive (CSS) with an iOS override,
    // so phones never flash an iframe before swapping.
    return (
      <div className="mt-6 space-y-3">
        <iframe
          title={t(locale, "pdfPreview")}
          src={`${src}#view=FitH`}
          className={`${isIOS ? "hidden" : "hidden md:block"} h-[80vh] min-h-[640px] w-full rounded border border-slate-200 bg-white`}
        />
        <div className={isIOS ? "block" : "md:hidden"}>
          <PdfOpenCard src={src} fileName={fileName} />
        </div>
        <FileActions src={src} fileName={fileName} />
      </div>
    );
  }

  // Non-PDF: download card (no iframe preview — browsers won't inline xlsx/docx
  // and would fall back to a "Save As…" dialog or a blank frame).
  const Icon = iconForExtension(ext);
  const typeLabel = humanLabelForExtension(ext);
  return (
    <div className="mt-6 rounded-md border border-slate-200 bg-[#F4F8FC] p-5">
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-white text-[#005CA9] ring-1 ring-slate-200">
          <Icon className="h-6 w-6" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-[#005CA9]">
            {typeLabel}
          </p>
          <p className="mt-1 break-words text-sm font-medium text-[#0F2849]">
            {fileName}
          </p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <FileActions src={src} fileName={fileName} />
      </div>
    </div>
  );
}

/**
 * Mobile/iOS PDF affordance: a tappable card that opens the document in the
 * device's native PDF viewer (new tab), where it gets fit-to-width, pinch-zoom
 * and scroll — instead of the zoomed, non-scrollable iframe iOS would render.
 */
function PdfOpenCard({ src, fileName }: { src: string; fileName: string }) {
  return (
    <a
      href={src}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-4 rounded-md border border-slate-200 bg-[#F4F8FC] p-5 no-underline transition hover:bg-[#E9F1FA] active:bg-[#E1ECF7]"
    >
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-white text-[#005CA9] ring-1 ring-slate-200">
        <FileText className="h-6 w-6" aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-[#005CA9]">Document PDF</p>
        <p className="mt-1 break-words text-sm font-medium text-[#0F2849]">{fileName}</p>
        <p className="mt-1 text-xs text-slate-500">Appuyez pour ouvrir le document</p>
      </div>
      <ExternalLink className="h-5 w-5 shrink-0 text-[#005CA9]" aria-hidden="true" />
    </a>
  );
}

function FileActions({ src, fileName }: { src: string; fileName: string }) {
  const locale = useLocale();
  return (
    <div className="flex flex-wrap items-center gap-2">
      <a
        href={src}
        download={fileName || true}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 rounded-md bg-[#005CA9] px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-[#004a8a]"
      >
        <Download className="h-3.5 w-3.5" /> {t(locale, "download")}
      </a>
    </div>
  );
}

/** Lowercase file extension (without dot) or "" when none. */
function extractExtension(keyOrUrl: string): string {
  const last = keyOrUrl.split(/[?#]/)[0].split("/").pop() ?? "";
  const dot = last.lastIndexOf(".");
  return dot >= 0 ? last.slice(dot + 1).toLowerCase() : "";
}

/** Last path segment, no query/fragment. Used as the displayed file name. */
function extractFileName(keyOrUrl: string): string {
  return decodeURIComponent(keyOrUrl.split(/[?#]/)[0].split("/").pop() ?? "");
}

function iconForExtension(ext: string) {
  if (["xlsx", "xls", "csv", "ods"].includes(ext)) return FileSpreadsheet;
  if (["docx", "doc", "odt", "rtf", "txt"].includes(ext)) return FileText;
  if (["pptx", "ppt", "odp"].includes(ext)) return FileType2;
  if (["zip", "rar", "7z", "tar", "gz"].includes(ext)) return FileArchive;
  return FileIcon;
}

function humanLabelForExtension(ext: string): string {
  if (["xlsx", "xls"].includes(ext)) return "Tableur Excel";
  if (ext === "csv") return "Fichier CSV";
  if (["docx", "doc"].includes(ext)) return "Document Word";
  if (["pptx", "ppt"].includes(ext)) return "Présentation PowerPoint";
  if (ext === "zip") return "Archive ZIP";
  if (ext === "rar") return "Archive RAR";
  if (ext) return `Fichier ${ext.toUpperCase()}`;
  return "Document";
}
